/* eslint-disable arrow-body-style */
import { useCallback, useEffect, useMemo, useState } from 'react'

import { equals } from 'ramda'

import { Box, Stack, Typography } from '@mui/material'

import { triggerPostMoveFlash } from '@atlaskit/pragmatic-drag-and-drop-flourish/trigger-post-move-flash'
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge'
import { getReorderDestinationIndex } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index'
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { reorder } from '@atlaskit/pragmatic-drag-and-drop/reorder'

import { Project } from '@common/config'
import { useLocalStorage } from '@common/utils'
import { getDefaultMetricsPositions } from '@common/utils/metricsUtils'
import portalMetrics from '@portal/pages/Dashboards/SmokeMetrics/metrics'
import restMetrics from '@rest/pages/SmokeMetrics/metrics'

import MetricsSelectorListItem from './MetricsSelectorListItem'
import { getMetricsPositionStorageKey, isItemData, ListContext } from './utils'

function getItemRegistry() {
  const registry = new Map()

  function register({ itemId, element }) {
    registry.set(itemId, element)

    return function unregister() {
      registry.delete(itemId)
    }
  }

  function getElement(itemId) {
    return registry.get(itemId) ?? null
  }

  return { register, getElement }
}

export default function MetricsSelector({
  allowedMetrics,
  selectedMetrics,
  setSelectedMetrics,
}) {
  const [metricsPositions, setMetricsPositions] = useLocalStorage(
    getMetricsPositionStorageKey(),
    getDefaultMetricsPositions(Project.isRest ? restMetrics : portalMetrics),
  )
  const [listState, setListState] = useState({
    items: allowedMetrics.sort((a, b) =>
      metricsPositions[a.apiValue] > metricsPositions[b.apiValue] ? 1 : -1,
    ),
    lastCardMoved: null,
  })
  const [registry] = useState(getItemRegistry)

  useEffect(() => {
    const updatedPositions = listState.items.reduce(
      (acc, item, index) => ({ ...acc, [item.apiValue]: index }),
      {},
    )
    if (!equals(metricsPositions, updatedPositions)) {
      setMetricsPositions(updatedPositions)
    }
  }, [listState])

  const [instanceId] = useState(() => Symbol('instance-id'))

  const { items, lastCardMoved } = listState

  const reorderItem = useCallback(
    ({ startIndex, indexOfTarget, closestEdgeOfTarget }) => {
      const finishIndex = getReorderDestinationIndex({
        startIndex,
        closestEdgeOfTarget,
        indexOfTarget,
        axis: 'vertical',
      })

      if (finishIndex === startIndex) {
        return
      }

      setListState((prev) => {
        const item = prev.items[startIndex]

        return {
          items: reorder({
            list: prev.items,
            startIndex,
            finishIndex,
          }),
          lastCardMoved: {
            item,
            previousIndex: startIndex,
            currentIndex: finishIndex,
            numberOfItems: prev.items.length,
          },
        }
      })
    },
    [],
  )

  useEffect(() => {
    return monitorForElements({
      canMonitor({ source }) {
        return isItemData(source.data) && source.data.instanceId === instanceId
      },
      onDrop({ location, source }) {
        const target = location.current.dropTargets[0]
        if (!target) {
          return
        }

        const sourceData = source.data
        const targetData = target.data
        if (!isItemData(sourceData) || !isItemData(targetData)) {
          return
        }

        const indexOfTarget = items.findIndex(
          (item) => item.apiValue === targetData.item.apiValue,
        )
        if (indexOfTarget < 0) {
          return
        }

        const closestEdgeOfTarget = extractClosestEdge(targetData)

        reorderItem({
          startIndex: sourceData.index,
          indexOfTarget,
          closestEdgeOfTarget,
        })
      },
    })
  }, [instanceId, items, reorderItem])

  useEffect(() => {
    if (lastCardMoved === null) {
      return
    }

    const element = registry.getElement(lastCardMoved.item.apiValue)
    if (element) {
      triggerPostMoveFlash(element)
    }
  }, [lastCardMoved, registry])

  const getListLength = useCallback(() => items.length, [items.length])

  const contextValue = useMemo(() => {
    return {
      registerItem: registry.register,
      reorderItem,
      instanceId,
      getListLength,
    }
  }, [registry.register, reorderItem, instanceId, getListLength])

  return (
    <Stack direction="column" px={2} py={1}>
      <Typography variant="h6" mb={1}>
        Available Metrics
      </Typography>
      <ListContext.Provider value={contextValue}>
        <Box>
          {items.map((item, index) => (
            <MetricsSelectorListItem
              key={item.apiValue}
              item={item}
              index={index}
              checked={selectedMetrics[item.apiValue]}
              onCheckChange={() => {
                setSelectedMetrics((prev) => ({
                  ...prev,
                  [item.apiValue]: !prev[item.apiValue],
                }))
              }}
            />
          ))}
        </Box>
      </ListContext.Provider>
    </Stack>
  )
}
